import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { Notification, type NotificationType } from './index';

interface NotificationContextType {
  showNotification: (
    message: string | string[],
    type: NotificationType,
    durationMs?: number
  ) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

type NotificationItem = {
  id: string;
  message: string | string[];
  type: NotificationType;
  durationMs: number;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const timeoutIds = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const notificationCounter = useRef(0);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(item => item.id !== id));

    const timeoutId = timeoutIds.current[id];
    if (timeoutId) {
      clearTimeout(timeoutId);
      delete timeoutIds.current[id];
    }
  }, []);

  const showNotification = useCallback(
    (
      message: string | string[],
      type: NotificationType,
      durationMs?: number
    ) => {
      const resolvedDurationMs =
        durationMs ?? (type === 'destructive' ? 20000 : 5000);
      notificationCounter.current += 1;
      const id = `notification-${notificationCounter.current}`;

      setNotifications(prev => [
        ...prev,
        {
          id,
          message,
          type,
          durationMs: resolvedDurationMs,
        },
      ]);

      timeoutIds.current[id] = setTimeout(() => {
        removeNotification(id);
      }, resolvedDurationMs);
    },
    [removeNotification]
  );

  useEffect(() => {
    return () => {
      Object.values(timeoutIds.current).forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      timeoutIds.current = {};
    };
  }, []);

  const handleClose = useCallback(
    (id: string) => {
      removeNotification(id);
    },
    [removeNotification]
  );

  const contextValue = useMemo(
    () => ({ showNotification }),
    [showNotification]
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {notifications.map((notification, index) => (
        <Notification
          key={notification.id}
          message={notification.message}
          type={notification.type}
          onClose={() => handleClose(notification.id)}
          topOffset={10 + index * 64}
        />
      ))}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotification must be used within a NotificationProvider'
    );
  }
  return context.showNotification;
};
